// Shared "last seen" freshness helpers for device cards / detail. Extracted
// from BoatDevices so the redesigned DeviceCard, DeviceDetail, and the Devices
// list all render presence the same way. A missing / unparseable timestamp is
// `never` — distinct from `cold`, so the UI can say "Never reported" instead of
// a misleading "Just now".

export type SeenState = 'fresh' | 'stale' | 'cold' | 'never';

/**
 * Map an age to a freshness band for SECONDS-CADENCE feeds — live telemetry
 * signals (signalCatalog, the alarm picker) and the boat's WS contact stamp
 * (Dashboard). Green (fresh) while data should be flowing; amber (stale)
 * when it may have gone quiet but isn't yet gone; red (cold) when the gap
 * far exceeds the cadence — likely offline.
 *
 * NOT the band for device inventory rows — those update on the 5-minute
 * per-device liveness cadence and read {@link deviceFreshnessClass} instead
 * (task lean_device_sync). The two questions genuinely differ: a signal
 * quiet for five minutes is stale; a device stamp five minutes old is one
 * liveness beat behind.
 */
export function freshnessClass(iso: string, now: number = Date.now()): 'fresh' | 'stale' | 'cold' {
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return 'cold';
  if (ms < 2 * 60_000) return 'fresh';
  if (ms < 10 * 60_000) return 'stale';
  return 'cold';
}

/**
 * The DEVICE-ROW freshness band, sized past the 5-minute per-device liveness
 * cadence + delivery margin (task lean_device_sync; the owner chose 5-minute
 * freshness over the old always-on 60 s digest, which was the bandwidth). A
 * boat on an older core still updates via its digests, far inside these
 * bands.
 */
export function deviceFreshnessClass(
  iso: string,
  now: number = Date.now()
): 'fresh' | 'stale' | 'cold' {
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return 'cold';
  if (ms < 7 * 60_000) return 'fresh';
  if (ms < 15 * 60_000) return 'stale';
  return 'cold';
}

/** Short relative-time wording — "5 min ago", "3 hr ago". `null` for missing. */
export function formatRelativeShort(iso: string, now: number = Date.now()): string | null {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  // A timestamp marginally in the future is NOT "never reported": callers hold `now`
  // as a 30 s tick (Devices.tsx) while the inventory refetches on the boat's 15 s
  // liveness cadence, so a freshly fetched `lastSeen` routinely lands ahead of it —
  // which used to flip every device to "No data". Clamp rather than bail; the same
  // guard keeps a boat clock slightly ahead of the viewer's from reading as never.
  // `formatRelative` below already does exactly this.
  const ms = Math.max(0, now - t);
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

/** Verbose relative-time — "5 minutes ago" — for page-level prose lines. */
export function formatRelative(iso: string | null, now: number = Date.now()): string {
  if (iso === null) return 'Never contacted';
  const ms = now - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return 'Just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

export interface SeenStatus {
  state: SeenState;
  label: string;
  status: string;
}

/**
 * Single source of truth for the "Last seen" affordance: dot state, the visible
 * short label, and a human status string for aria-label / title (so health is
 * legible without color and to screen readers).
 */
export function seenStatus(iso: string, now: number = Date.now()): SeenStatus {
  const short = formatRelativeShort(iso, now);
  if (short === null) return { state: 'never', label: 'Never', status: 'Never reported' };
  const state = deviceFreshnessClass(iso, now);
  const status =
    state === 'cold'
      ? `No recent data — last seen ${short.toLowerCase()}`
      : `Online — last seen ${short.toLowerCase()}`;
  return { state, label: short, status };
}

/** Compact human file size for manual links — "2.3 MB", "512 KB". */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = n;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const rounded = i === 0 || value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[i]}`;
}
